import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import DOMPurify from "dompurify";
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Heading1,
  Heading2,
  Heading3,
  Pilcrow,
  List,
  ListOrdered,
  Quote,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Link as LinkIcon,
  Unlink,
  Image as ImageIcon,
  Minus,
  Eraser,
  Code,
  Eye,
  Type,
  Trash2,
  Upload,
  X,
  Layers,
  ChevronDown,
  ChevronUp,
  ImagePlus,
  Maximize,
  Check,
} from "lucide-react";
import { compressImageToWebP } from "../../utils/mediaOptimizer.ts";

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  onSetCoverImage?: (url: string) => void;
}

// Sanitization config allowing clean images and essential styling
const sanitizeEditorHtml = (raw: string): string => {
  if (!raw) return "";
  return DOMPurify.sanitize(raw, {
    ADD_TAGS: ["figure", "figcaption", "picture", "source", "iframe", "img"],
    ADD_ATTR: [
      "target",
      "style",
      "class",
      "data-align",
      "data-width",
      "referrerpolicy",
      "loading",
      "width",
      "height",
      "src",
      "alt",
      "title",
    ],
  });
};

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = "Tulis isi naskah artikel di sini...",
  onSetCoverImage,
}) => {
  const [mode, setMode] = useState<"visual" | "html">("visual");
  const [activeBlock, setActiveBlock] = useState<"h1" | "h2" | "h3" | "p" | "blockquote">("p");
  const [activeInline, setActiveInline] = useState<{
    bold: boolean;
    italic: boolean;
    underline: boolean;
    strike: boolean;
  }>({
    bold: false,
    italic: false,
    underline: false,
    strike: false,
  });

  // Selected image inside the contentEditable editor
  const [selectedImageEl, setSelectedImageEl] = useState<HTMLImageElement | null>(null);
  const [selectedImgCoords, setSelectedImgCoords] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  // Toggle for in-body image drawer
  const [showImageManager, setShowImageManager] = useState(true);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const editorRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isInternalChange = useRef(false);

  // Sync content with editorRef
  useEffect(() => {
    if (mode === "visual" && editorRef.current) {
      const currentHtml = editorRef.current.innerHTML;
      const sanitized = sanitizeEditorHtml(value || "");
      if (currentHtml !== sanitized && !isInternalChange.current) {
        editorRef.current.innerHTML = sanitized || "<p><br></p>";
      }
      isInternalChange.current = false;
    }
  }, [value, mode]);

  // Extract all <img> tags from current value for in-body gallery manager
  const embeddedImages = useMemo(() => {
    if (!value) return [];
    if (typeof document === "undefined") return [];
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(value, "text/html");
      const imgs = Array.from(doc.querySelectorAll("img"));
      return imgs
        .map((img, idx) => ({
          id: `img-${idx}`,
          src: img.getAttribute("src") || "",
          alt: img.getAttribute("alt") || `Foto #${idx + 1}`,
          index: idx,
        }))
        .filter((item) => item.src.trim() !== "");
    } catch {
      return [];
    }
  }, [value]);

  // Update floating toolbar position over selected image
  const updateImageOverlayPosition = useCallback(() => {
    if (!selectedImageEl || !containerRef.current || !editorRef.current) {
      setSelectedImgCoords(null);
      return;
    }

    try {
      const imgRect = selectedImageEl.getBoundingClientRect();
      const containerRect = containerRef.current.getBoundingClientRect();

      setSelectedImgCoords({
        top: imgRect.top - containerRect.top + editorRef.current.scrollTop,
        left: imgRect.left - containerRect.left,
        width: imgRect.width,
      });
    } catch {
      setSelectedImgCoords(null);
    }
  }, [selectedImageEl]);

  // Handle click on editor elements (especially <img> tags)
  const handleEditorClick = (e: React.MouseEvent<HTMLDivElement>) => {
    checkCurrentFormatting();
    const target = e.target as HTMLElement;

    if (target && target.tagName === "IMG") {
      const imgEl = target as HTMLImageElement;
      setSelectedImageEl(imgEl);

      // Add a prominent visual outline to the selected image
      editorRef.current?.querySelectorAll("img").forEach((el) => {
        el.classList.remove("ring-4", "ring-rose-500", "ring-offset-2", "shadow-xl");
      });
      imgEl.classList.add("ring-4", "ring-rose-500", "ring-offset-2", "shadow-xl");
    } else {
      // Clicked outside image
      if (selectedImageEl) {
        selectedImageEl.classList.remove("ring-4", "ring-rose-500", "ring-offset-2", "shadow-xl");
        setSelectedImageEl(null);
        setSelectedImgCoords(null);
      }
    }
  };

  useEffect(() => {
    updateImageOverlayPosition();
  }, [selectedImageEl, updateImageOverlayPosition]);

  // Update formatting indicators on selection change
  const checkCurrentFormatting = useCallback(() => {
    if (mode !== "visual" || !editorRef.current) return;

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    let node: Node | null = selection.anchorNode;
    let detectedBlock: "h1" | "h2" | "h3" | "p" | "blockquote" = "p";

    while (node && node !== editorRef.current) {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const tagName = (node as HTMLElement).tagName.toLowerCase();
        if (tagName === "h1") {
          detectedBlock = "h1";
          break;
        } else if (tagName === "h2") {
          detectedBlock = "h2";
          break;
        } else if (tagName === "h3") {
          detectedBlock = "h3";
          break;
        } else if (tagName === "blockquote") {
          detectedBlock = "blockquote";
          break;
        } else if (tagName === "p") {
          detectedBlock = "p";
          break;
        }
      }
      node = node.parentNode;
    }

    setActiveBlock(detectedBlock);

    try {
      setActiveInline({
        bold: document.queryCommandState("bold"),
        italic: document.queryCommandState("italic"),
        underline: document.queryCommandState("underline"),
        strike: document.queryCommandState("strikeThrough"),
      });
    } catch {
      // ignore
    }
  }, [mode]);

  useEffect(() => {
    const handleDocSelectionChange = () => {
      if (editorRef.current && document.activeElement === editorRef.current) {
        checkCurrentFormatting();
      }
    };
    document.addEventListener("selectionchange", handleDocSelectionChange);
    return () => {
      document.removeEventListener("selectionchange", handleDocSelectionChange);
    };
  }, [checkCurrentFormatting]);

  const handleInput = () => {
    if (editorRef.current) {
      isInternalChange.current = true;
      const cleanHtml = sanitizeEditorHtml(editorRef.current.innerHTML);
      onChange(cleanHtml);
      checkCurrentFormatting();
    }
  };

  const execCmd = (command: string, arg: string | undefined = undefined) => {
    if (mode !== "visual" || !editorRef.current) return;
    editorRef.current.focus();
    try {
      document.execCommand(command, false, arg);
    } catch (e) {
      console.warn("execCmd error:", e);
    }
    handleInput();
  };

  // Robust format block handler for H1, H2, H3, P, Blockquote
  const handleFormatBlock = (targetTag: "h1" | "h2" | "h3" | "p" | "blockquote") => {
    if (mode !== "visual" || !editorRef.current) return;
    editorRef.current.focus();

    const tag = targetTag.toLowerCase();
    const selection = window.getSelection();

    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      let container: Node | null = range.commonAncestorContainer;

      if (container.nodeType === Node.TEXT_NODE) {
        container = container.parentNode;
      }

      let blockElement: HTMLElement | null = null;
      let curr = container;
      while (curr && curr !== editorRef.current) {
        if (curr.nodeType === Node.ELEMENT_NODE) {
          const tn = (curr as HTMLElement).tagName.toUpperCase();
          if (["H1", "H2", "H3", "H4", "H5", "H6", "P", "BLOCKQUOTE", "DIV"].includes(tn)) {
            blockElement = curr as HTMLElement;
            break;
          }
        }
        curr = curr.parentNode;
      }

      if (blockElement && blockElement !== editorRef.current) {
        const currentTagName = blockElement.tagName.toLowerCase();
        if (currentTagName !== tag) {
          const newEl = document.createElement(tag);
          newEl.innerHTML = blockElement.innerHTML || "<br>";
          blockElement.parentNode?.replaceChild(newEl, blockElement);

          try {
            const newRange = document.createRange();
            newRange.selectNodeContents(newEl);
            newRange.collapse(false);
            selection.removeAllRanges();
            selection.addRange(newRange);
          } catch (e) {
            // ignore range error
          }

          setActiveBlock(targetTag);
          handleInput();
          return;
        } else {
          if (tag !== "p") {
            const newP = document.createElement("p");
            newP.innerHTML = blockElement.innerHTML || "<br>";
            blockElement.parentNode?.replaceChild(newP, blockElement);

            try {
              const newRange = document.createRange();
              newRange.selectNodeContents(newP);
              newRange.collapse(false);
              selection.removeAllRanges();
              selection.addRange(newRange);
            } catch (e) {}

            setActiveBlock("p");
            handleInput();
            return;
          }
        }
      }
    }

    try {
      const ok = document.execCommand("formatBlock", false, `<${tag}>`);
      if (!ok) {
        document.execCommand("formatBlock", false, tag);
      }
    } catch {
      try {
        document.execCommand("formatBlock", false, tag.toUpperCase());
      } catch (e) {}
    }

    setActiveBlock(targetTag);
    handleInput();
  };

  const handleInsertLink = () => {
    const url = prompt("Masukkan URL Tautan (contoh: https://domain.com/layanan):", "https://");
    if (url && url.trim() !== "" && url !== "https://") {
      execCmd("createLink", url.trim());
    }
  };

  const handleInsertImageUrl = () => {
    const url = prompt("Masukkan URL Gambar (contoh: https://domain.com/foto.jpg):", "https://");
    if (url && url.trim() !== "" && url !== "https://" && url !== "http://") {
      const trimmedUrl = url.trim();
      if (!trimmedUrl.startsWith("http://") && !trimmedUrl.startsWith("https://") && !trimmedUrl.startsWith("data:image/")) {
        alert("URL gambar tidak valid. URL harus diawali dengan http:// atau https://");
        return;
      }
      execCmd("insertImage", trimmedUrl);
    }
  };

  // Upload image from file and insert directly into article body
  const handleUploadImageFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploadingImage(true);
    try {
      const res = await compressImageToWebP(file, 1200, 0.82);
      if (mode === "visual" && editorRef.current) {
        editorRef.current.focus();
        const imgTag = `<p><img src="${res.dataUrl}" alt="${file.name}" class="rounded-xl my-4 max-w-full shadow-md" style="display: block; margin-left: auto; margin-right: auto; max-width: 100%; height: auto;" /></p><p><br></p>`;
        document.execCommand("insertHTML", false, imgTag);
        handleInput();
      } else {
        const imgTag = `\n<img src="${res.dataUrl}" alt="${file.name}" class="rounded-xl my-4" style="max-width: 100%; height: auto;" />\n`;
        onChange((value || "") + imgTag);
      }
    } catch (err) {
      alert("Gagal mengunggah foto. Silakan coba format lain.");
    } finally {
      setIsUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // 1. Delete currently selected image element directly from editor
  const handleRemoveSelectedImage = () => {
    if (!selectedImageEl) return;

    const parent = selectedImageEl.parentElement;
    if (
      parent &&
      (parent.tagName === "FIGURE" ||
        (parent.tagName === "P" && (parent.textContent || "").trim() === "" && parent.children.length === 1))
    ) {
      parent.remove();
    } else {
      selectedImageEl.remove();
    }

    setSelectedImageEl(null);
    setSelectedImgCoords(null);
    handleInput();
  };

  // 2. Align / Format currently selected image
  const handleAlignSelectedImage = (alignment: "left" | "center" | "right" | "full") => {
    if (!selectedImageEl) return;

    if (alignment === "left") {
      selectedImageEl.style.float = "left";
      selectedImageEl.style.margin = "0 1.25rem 1.25rem 0";
      selectedImageEl.style.display = "inline";
      selectedImageEl.style.maxWidth = "50%";
    } else if (alignment === "right") {
      selectedImageEl.style.float = "right";
      selectedImageEl.style.margin = "0 0 1.25rem 1.25rem";
      selectedImageEl.style.display = "inline";
      selectedImageEl.style.maxWidth = "50%";
    } else if (alignment === "center") {
      selectedImageEl.style.float = "none";
      selectedImageEl.style.margin = "1.5rem auto";
      selectedImageEl.style.display = "block";
      selectedImageEl.style.maxWidth = "80%";
    } else if (alignment === "full") {
      selectedImageEl.style.float = "none";
      selectedImageEl.style.margin = "1.5rem auto";
      selectedImageEl.style.display = "block";
      selectedImageEl.style.maxWidth = "100%";
      selectedImageEl.style.width = "100%";
    }

    handleInput();
    updateImageOverlayPosition();
  };

  // 3. Delete specific image by index from the entire document
  const handleDeleteImageByIndex = (targetIndex: number) => {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(value || "", "text/html");
      const imgs = doc.querySelectorAll("img");
      if (imgs[targetIndex]) {
        const target = imgs[targetIndex];
        const parent = target.parentElement;
        if (
          parent &&
          (parent.tagName === "FIGURE" ||
            (parent.tagName === "P" &&
              (parent.textContent || "").trim() === "" &&
              parent.children.length === 1))
        ) {
          parent.remove();
        } else {
          target.remove();
        }

        const newHtml = sanitizeEditorHtml(doc.body.innerHTML);
        onChange(newHtml);
        setSelectedImageEl(null);
        setSelectedImgCoords(null);
      }
    } catch (err) {
      console.error("Gagal menghapus gambar:", err);
    }
  };

  // 4. Delete ALL images embedded inside article content
  const handleDeleteAllImages = () => {
    if (!window.confirm("Apakah Anda yakin ingin MENGHAPUS SEMUA foto yang ada di dalam naskah artikel ini?")) {
      return;
    }

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(value || "", "text/html");
      const figures = doc.querySelectorAll("figure");
      figures.forEach((f) => f.remove());

      const imgs = doc.querySelectorAll("img");
      imgs.forEach((img) => {
        const parent = img.parentElement;
        if (
          parent &&
          parent.tagName === "P" &&
          (parent.textContent || "").trim() === "" &&
          parent.children.length === 1
        ) {
          parent.remove();
        } else {
          img.remove();
        }
      });

      const cleanedHtml = doc.body.innerHTML.replace(/<p>\s*(<br\s*\/?>)?\s*<\/p>/gi, "");
      const newHtml = sanitizeEditorHtml(cleanedHtml);
      onChange(newHtml);
      setSelectedImageEl(null);
      setSelectedImgCoords(null);
    } catch (err) {
      console.error("Gagal menghapus semua gambar:", err);
    }
  };

  // 5. Scroll and highlight image in visual editor
  const handleScrollToImage = (index: number) => {
    if (mode !== "visual" || !editorRef.current) return;
    const imgs = editorRef.current.querySelectorAll("img");
    if (imgs[index]) {
      const img = imgs[index] as HTMLImageElement;
      img.scrollIntoView({ behavior: "smooth", block: "center" });
      setSelectedImageEl(img);
      editorRef.current.querySelectorAll("img").forEach((el) => {
        el.classList.remove("ring-4", "ring-rose-500", "ring-offset-2", "shadow-xl");
      });
      img.classList.add("ring-4", "ring-rose-500", "ring-offset-2", "shadow-xl");
    }
  };

  // Helper for quick inserting HTML tag in code/HTML mode
  const insertHtmlTag = (openTag: string, closeTag: string) => {
    const textarea = document.getElementById("wp-html-textarea") as HTMLTextAreaElement;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);
    const replacement = `${openTag}${selectedText}${closeTag}`;

    const newValue = value.substring(0, start) + replacement + value.substring(end);
    onChange(newValue);

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + openTag.length, end + openTag.length);
    }, 0);
  };

  // KeyDown Handler: Backspace/Delete removes selected image
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (selectedImageEl && (e.key === "Backspace" || e.key === "Delete")) {
      e.preventDefault();
      handleRemoveSelectedImage();
      return;
    }

    if (e.key === "Enter" && !e.shiftKey) {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        let node = selection.anchorNode;
        let headingBlock: HTMLElement | null = null;

        while (node && node !== editorRef.current) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const tn = (node as HTMLElement).tagName.toLowerCase();
            if (["h1", "h2", "h3", "blockquote"].includes(tn)) {
              headingBlock = node as HTMLElement;
              break;
            }
          }
          node = node.parentNode;
        }

        if (headingBlock) {
          e.preventDefault();
          const p = document.createElement("p");
          p.innerHTML = "<br>";

          if (headingBlock.nextSibling) {
            headingBlock.parentNode?.insertBefore(p, headingBlock.nextSibling);
          } else {
            headingBlock.parentNode?.appendChild(p);
          }

          const newRange = document.createRange();
          newRange.setStart(p, 0);
          newRange.setEnd(p, 0);
          newRange.collapse(true);
          selection.removeAllRanges();
          selection.addRange(newRange);

          setActiveBlock("p");
          handleInput();
        }
      }
    }
  };

  return (
    <div
      ref={containerRef}
      className="border border-slate-300 rounded-2xl overflow-hidden bg-white shadow-sm relative"
    >
      {/* Hidden File Input for Image Upload */}
      <input
        type="file"
        ref={fileInputRef}
        accept="image/*"
        onChange={handleUploadImageFile}
        className="hidden"
      />

      {/* Editor CSS & Heading Rules */}
      <style>{`
        [contenteditable]:empty:before {
          content: attr(data-placeholder);
          color: #94a3b8;
          pointer-events: none;
          display: block;
        }
        .article-visual-editor {
          color: #1e293b !important;
          font-family: inherit;
        }
        .article-visual-editor img {
          max-width: 100%;
          height: auto;
          border-radius: 0.75rem;
          margin: 1.25rem auto;
          cursor: pointer;
          transition: all 0.2s ease-in-out;
        }
        .article-visual-editor img:hover {
          outline: 3px dashed #f43f5e;
          outline-offset: 4px;
        }
        .article-visual-editor h1 {
          font-size: 2.25rem !important;
          font-weight: 900 !important;
          color: #0f172a !important;
          margin-top: 1.5rem !important;
          margin-bottom: 0.75rem !important;
          line-height: 1.25 !important;
          display: block !important;
          border-bottom: 2px solid #e2e8f0;
          padding-bottom: 0.35rem;
        }
        .article-visual-editor h2 {
          font-size: 1.75rem !important;
          font-weight: 800 !important;
          color: #0f172a !important;
          margin-top: 1.35rem !important;
          margin-bottom: 0.65rem !important;
          line-height: 1.3 !important;
          display: block !important;
          border-bottom: 1px solid #f1f5f9;
          padding-bottom: 0.25rem;
        }
        .article-visual-editor h3 {
          font-size: 1.35rem !important;
          font-weight: 700 !important;
          color: #1e293b !important;
          margin-top: 1.2rem !important;
          margin-bottom: 0.5rem !important;
          line-height: 1.35 !important;
          display: block !important;
        }
        .article-visual-editor p {
          font-size: 1.05rem !important;
          line-height: 1.8 !important;
          margin-bottom: 1.15rem !important;
          color: #1e293b !important;
        }
        .article-visual-editor blockquote {
          border-left: 4px solid #0284c7 !important;
          background-color: #f0f9ff !important;
          padding: 0.85rem 1.15rem !important;
          margin-top: 1.25rem !important;
          margin-bottom: 1.25rem !important;
          font-style: italic !important;
          color: #334155 !important;
          border-radius: 0 0.5rem 0.5rem 0 !important;
        }
        .article-visual-editor ul {
          list-style-type: disc !important;
          padding-left: 1.5rem !important;
          margin-bottom: 1.25rem !important;
          color: #1e293b !important;
        }
        .article-visual-editor ol {
          list-style-type: decimal !important;
          padding-left: 1.5rem !important;
          margin-bottom: 1.25rem !important;
          color: #1e293b !important;
        }
        .article-visual-editor a {
          color: #0284c7 !important;
          text-decoration: underline !important;
          font-weight: 600 !important;
        }
      `}</style>

      {/* Top Bar Header & Mode Selector */}
      <div className="bg-slate-900 text-white p-2.5 sm:px-4 flex flex-wrap items-center justify-between gap-2 border-b border-slate-800">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-lg bg-blue-500/20 text-blue-400 flex items-center justify-center font-black text-xs">
            <Type className="w-3.5 h-3.5" />
          </div>
          <div>
            <span className="text-xs font-black text-white tracking-wide block">
              Modern Rich Text Editor
            </span>
            <span className="text-[10px] text-slate-400 font-medium">
              Struktur Heading SEO Dinamis & Manajemen Foto Fleksibel
            </span>
          </div>
        </div>

        {/* Editor Mode Tabs (Visual vs HTML) */}
        <div className="flex items-center bg-slate-800 p-0.5 rounded-lg text-xs font-bold border border-slate-700">
          <button
            type="button"
            onClick={() => setMode("visual")}
            className={`px-3 py-1 rounded-md flex items-center gap-1.5 transition cursor-pointer ${
              mode === "visual"
                ? "bg-blue-600 text-white shadow-xs font-extrabold"
                : "text-slate-300 hover:text-white"
            }`}
          >
            <Eye className="w-3.5 h-3.5" />
            Visual (WYSIWYG)
          </button>
          <button
            type="button"
            onClick={() => {
              setSelectedImageEl(null);
              setMode("html");
            }}
            className={`px-3 py-1 rounded-md flex items-center gap-1.5 transition cursor-pointer ${
              mode === "html"
                ? "bg-blue-600 text-white shadow-xs font-extrabold"
                : "text-slate-300 hover:text-white"
            }`}
          >
            <Code className="w-3.5 h-3.5" />
            Kode HTML
          </button>
        </div>
      </div>

      {/* Toolbar Controls */}
      {mode === "visual" ? (
        <div className="bg-slate-50 border-b border-slate-200 p-2 sm:p-2.5 flex flex-wrap items-center gap-1.5 sm:gap-2 text-slate-700 select-none">
          {/* Active Heading / Paragraph Buttons */}
          <div className="flex items-center gap-1 bg-white p-1 rounded-xl border border-slate-200 shadow-2xs">
            {/* H1 Button */}
            <button
              type="button"
              title="Judul Utama Halaman (H1)"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleFormatBlock("h1")}
              className={`px-2.5 py-1.5 rounded-lg text-xs font-extrabold flex items-center gap-1.5 transition-all cursor-pointer ${
                activeBlock === "h1"
                  ? "bg-slate-900 text-white ring-2 ring-slate-900 ring-offset-1 shadow-xs"
                  : "bg-transparent text-slate-600 hover:bg-slate-100 hover:text-slate-900"
              }`}
            >
              <Heading1 className="w-4 h-4 shrink-0" />
              <span className="text-[11px]">H1</span>
            </button>

            {/* H2 Button */}
            <button
              type="button"
              title="Subjudul Utama Artikel (H2) - Dianjurkan untuk SEO"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleFormatBlock("h2")}
              className={`px-2.5 py-1.5 rounded-lg text-xs font-extrabold flex items-center gap-1.5 transition-all cursor-pointer ${
                activeBlock === "h2"
                  ? "bg-blue-600 text-white ring-2 ring-blue-600 ring-offset-1 shadow-xs"
                  : "bg-transparent text-slate-600 hover:bg-slate-100 hover:text-slate-900"
              }`}
            >
              <Heading2 className="w-4 h-4 shrink-0" />
              <span className="text-[11px]">H2</span>
            </button>

            {/* H3 Button */}
            <button
              type="button"
              title="Sub-bagian / Poin Spesifik (H3)"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleFormatBlock("h3")}
              className={`px-2.5 py-1.5 rounded-lg text-xs font-extrabold flex items-center gap-1.5 transition-all cursor-pointer ${
                activeBlock === "h3"
                  ? "bg-amber-600 text-white ring-2 ring-amber-600 ring-offset-1 shadow-xs"
                  : "bg-transparent text-slate-600 hover:bg-slate-100 hover:text-slate-900"
              }`}
            >
              <Heading3 className="w-4 h-4 shrink-0" />
              <span className="text-[11px]">H3</span>
            </button>

            {/* Paragraph Button */}
            <button
              type="button"
              title="Paragraf Normal (Teks Biasa)"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleFormatBlock("p")}
              className={`px-2.5 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1 transition-all cursor-pointer ${
                activeBlock === "p"
                  ? "bg-slate-200 text-slate-900 font-extrabold"
                  : "bg-transparent text-slate-500 hover:bg-slate-100 hover:text-slate-900"
              }`}
            >
              <Pilcrow className="w-3.5 h-3.5 shrink-0" />
              <span className="text-[11px]">Paragraf</span>
            </button>
          </div>

          <div className="h-5 w-px bg-slate-300 mx-0.5 hidden sm:block" />

          {/* Inline Text Formatting (Bold, Italic, Underline, Strikethrough) */}
          <div className="flex items-center gap-0.5 bg-white p-1 rounded-xl border border-slate-200 shadow-2xs">
            <button
              type="button"
              title="Tebal (Bold) - Ctrl+B"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => execCmd("bold")}
              className={`p-1.5 rounded-lg transition cursor-pointer ${
                activeInline.bold
                  ? "bg-slate-900 text-white font-black"
                  : "hover:bg-slate-100 text-slate-700"
              }`}
            >
              <Bold className="w-4 h-4" />
            </button>
            <button
              type="button"
              title="Miring (Italic) - Ctrl+I"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => execCmd("italic")}
              className={`p-1.5 rounded-lg transition cursor-pointer ${
                activeInline.italic
                  ? "bg-slate-900 text-white font-black"
                  : "hover:bg-slate-100 text-slate-700"
              }`}
            >
              <Italic className="w-4 h-4" />
            </button>
            <button
              type="button"
              title="Garis Bawah (Underline) - Ctrl+U"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => execCmd("underline")}
              className={`p-1.5 rounded-lg transition cursor-pointer ${
                activeInline.underline
                  ? "bg-slate-900 text-white font-black"
                  : "hover:bg-slate-100 text-slate-700"
              }`}
            >
              <Underline className="w-4 h-4" />
            </button>
            <button
              type="button"
              title="Coret (Strikethrough)"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => execCmd("strikeThrough")}
              className={`p-1.5 rounded-lg transition cursor-pointer ${
                activeInline.strike
                  ? "bg-slate-900 text-white font-black"
                  : "hover:bg-slate-100 text-slate-700"
              }`}
            >
              <Strikethrough className="w-4 h-4" />
            </button>
          </div>

          <div className="h-5 w-px bg-slate-300 mx-0.5 hidden sm:block" />

          {/* Alignments */}
          <div className="flex items-center gap-0.5 bg-white p-1 rounded-xl border border-slate-200 shadow-2xs">
            <button
              type="button"
              title="Rata Kiri"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => execCmd("justifyLeft")}
              className="p-1.5 hover:bg-slate-100 text-slate-700 rounded-lg transition cursor-pointer"
            >
              <AlignLeft className="w-4 h-4" />
            </button>
            <button
              type="button"
              title="Rata Tengah"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => execCmd("justifyCenter")}
              className="p-1.5 hover:bg-slate-100 text-slate-700 rounded-lg transition cursor-pointer"
            >
              <AlignCenter className="w-4 h-4" />
            </button>
            <button
              type="button"
              title="Rata Kanan"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => execCmd("justifyRight")}
              className="p-1.5 hover:bg-slate-100 text-slate-700 rounded-lg transition cursor-pointer"
            >
              <AlignRight className="w-4 h-4" />
            </button>
            <button
              type="button"
              title="Rata Kanan Kiri (Justify)"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => execCmd("justifyFull")}
              className="p-1.5 hover:bg-slate-100 text-slate-700 rounded-lg transition cursor-pointer"
            >
              <AlignJustify className="w-4 h-4" />
            </button>
          </div>

          <div className="h-5 w-px bg-slate-300 mx-0.5 hidden sm:block" />

          {/* Lists & Quote */}
          <div className="flex items-center gap-0.5 bg-white p-1 rounded-xl border border-slate-200 shadow-2xs">
            <button
              type="button"
              title="Daftar Simbol (Bulleted List)"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => execCmd("insertUnorderedList")}
              className="p-1.5 hover:bg-slate-100 text-slate-700 rounded-lg transition cursor-pointer"
            >
              <List className="w-4 h-4" />
            </button>
            <button
              type="button"
              title="Daftar Angka (Numbered List)"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => execCmd("insertOrderedList")}
              className="p-1.5 hover:bg-slate-100 text-slate-700 rounded-lg transition cursor-pointer"
            >
              <ListOrdered className="w-4 h-4" />
            </button>
            <button
              type="button"
              title="Kutipan (Blockquote)"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleFormatBlock("blockquote")}
              className={`p-1.5 rounded-lg transition cursor-pointer ${
                activeBlock === "blockquote"
                  ? "bg-sky-600 text-white"
                  : "hover:bg-slate-100 text-slate-700"
              }`}
            >
              <Quote className="w-4 h-4" />
            </button>
          </div>

          <div className="h-5 w-px bg-slate-300 mx-0.5 hidden sm:block" />

          {/* Link, Image Inserters, Divider, Eraser */}
          <div className="flex items-center gap-0.5 bg-white p-1 rounded-xl border border-slate-200 shadow-2xs">
            <button
              type="button"
              title="Sisipkan Tautan (Link)"
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleInsertLink}
              className="p-1.5 hover:bg-blue-50 text-blue-600 rounded-lg transition cursor-pointer"
            >
              <LinkIcon className="w-4 h-4" />
            </button>
            <button
              type="button"
              title="Hapus Tautan"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => execCmd("unlink")}
              className="p-1.5 hover:bg-slate-100 text-slate-600 rounded-lg transition cursor-pointer"
            >
              <Unlink className="w-4 h-4" />
            </button>

            {/* Upload image from file */}
            <button
              type="button"
              title="Unggah Foto dari Perangkat (Otomatis WebP)"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploadingImage}
              className="p-1.5 hover:bg-emerald-50 text-emerald-700 rounded-lg transition cursor-pointer flex items-center gap-1"
            >
              <Upload className="w-4 h-4" />
            </button>

            {/* Insert image from URL */}
            <button
              type="button"
              title="Sisipkan Foto dari URL Web"
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleInsertImageUrl}
              className="p-1.5 hover:bg-emerald-50 text-emerald-700 rounded-lg transition cursor-pointer"
            >
              <ImageIcon className="w-4 h-4" />
            </button>

            <button
              type="button"
              title="Garis Pemisah (Horizontal Rule)"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => execCmd("insertHorizontalRule")}
              className="p-1.5 hover:bg-slate-100 text-slate-600 rounded-lg transition cursor-pointer"
            >
              <Minus className="w-4 h-4" />
            </button>
            <button
              type="button"
              title="Hapus Format Teks"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => execCmd("removeFormat")}
              className="p-1.5 hover:bg-rose-50 text-rose-600 rounded-lg transition cursor-pointer"
            >
              <Eraser className="w-4 h-4" />
            </button>
          </div>

          {/* Quick "Hapus Semua Foto di Naskah" Toolbar Button if images exist */}
          {embeddedImages.length > 0 && (
            <button
              type="button"
              onClick={handleDeleteAllImages}
              className="ml-auto px-2.5 py-1 bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 rounded-lg text-xs font-bold flex items-center gap-1.5 transition cursor-pointer"
              title="Bersihkan semua foto yang ada di dalam naskah sekaligus"
            >
              <Trash2 className="w-3.5 h-3.5 text-rose-600" />
              <span>Hapus Semua Foto Naskah ({embeddedImages.length})</span>
            </button>
          )}
        </div>
      ) : (
        /* HTML Code Mode Toolbar Buttons */
        <div className="bg-slate-800 border-b border-slate-700 p-2 flex flex-wrap items-center justify-between gap-2 text-slate-300">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] font-bold text-slate-400 mr-1 flex items-center gap-1">
              <Code className="w-3.5 h-3.5 text-blue-400" />
              Tag Cepat:
            </span>
            <button
              type="button"
              onClick={() => insertHtmlTag("<h2>", "</h2>")}
              className="px-2.5 py-1 bg-slate-700 text-white rounded text-xs font-bold hover:bg-blue-600 transition cursor-pointer"
            >
              &lt;h2&gt;
            </button>
            <button
              type="button"
              onClick={() => insertHtmlTag("<h3>", "</h3>")}
              className="px-2.5 py-1 bg-slate-700 text-white rounded text-xs font-bold hover:bg-blue-600 transition cursor-pointer"
            >
              &lt;h3&gt;
            </button>
            <button
              type="button"
              onClick={() => insertHtmlTag("<p>", "</p>")}
              className="px-2.5 py-1 bg-slate-700 text-white rounded text-xs font-bold hover:bg-slate-600 transition cursor-pointer"
            >
              &lt;p&gt;
            </button>
            <button
              type="button"
              onClick={() => insertHtmlTag("<b>", "</b>")}
              className="px-2.5 py-1 bg-slate-700 text-white rounded text-xs font-bold hover:bg-slate-600 transition cursor-pointer"
            >
              &lt;b&gt;
            </button>
            <button
              type="button"
              onClick={() => insertHtmlTag("<i>", "</i>")}
              className="px-2.5 py-1 bg-slate-700 text-white rounded text-xs italic font-bold hover:bg-slate-600 transition cursor-pointer"
            >
              &lt;i&gt;
            </button>
            <button
              type="button"
              onClick={() => insertHtmlTag("<blockquote>", "</blockquote>")}
              className="px-2.5 py-1 bg-slate-700 text-white rounded text-xs font-bold hover:bg-slate-600 transition cursor-pointer"
            >
              &lt;quote&gt;
            </button>
            <button
              type="button"
              onClick={() => insertHtmlTag('<a href="https://">', "</a>")}
              className="px-2.5 py-1 bg-slate-700 text-sky-400 rounded text-xs font-bold hover:bg-slate-600 transition cursor-pointer"
            >
              &lt;link&gt;
            </button>
          </div>

          {/* Remove all img tags button in HTML mode */}
          {embeddedImages.length > 0 && (
            <button
              type="button"
              onClick={handleDeleteAllImages}
              className="px-2.5 py-1 bg-rose-900/60 hover:bg-rose-800 text-rose-200 border border-rose-700/80 rounded-lg text-xs font-bold flex items-center gap-1.5 transition cursor-pointer"
            >
              <Trash2 className="w-3.5 h-3.5 text-rose-300" />
              <span>Bersihkan Tag &lt;img&gt; ({embeddedImages.length})</span>
            </button>
          )}
        </div>
      )}

      {/* FLOATING ACTION TOOLBAR OVER SELECTED IMAGE */}
      {selectedImageEl && selectedImgCoords && mode === "visual" && (
        <div
          className="absolute z-30 flex items-center gap-1.5 bg-slate-900/95 backdrop-blur-md text-white px-3 py-2 rounded-2xl shadow-2xl border border-slate-700 animate-in fade-in zoom-in duration-150"
          style={{
            top: `${Math.max(10, selectedImgCoords.top - 48)}px`,
            left: `${Math.max(10, Math.min(selectedImgCoords.left, (containerRef.current?.offsetWidth || 500) - 340))}px`,
          }}
        >
          {/* HIGH-VISIBILITY DELETE BUTTON */}
          <button
            type="button"
            onClick={handleRemoveSelectedImage}
            className="px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-xs font-black flex items-center gap-1.5 shadow-md transition cursor-pointer"
            title="Hapus foto ini dari naskah artikel"
          >
            <Trash2 className="w-4 h-4" />
            <span>Hapus Foto Ini</span>
          </button>

          <div className="h-4 w-px bg-slate-700 mx-0.5" />

          {/* Quick Align Options */}
          <button
            type="button"
            onClick={() => handleAlignSelectedImage("left")}
            className="p-1.5 hover:bg-slate-800 text-slate-300 hover:text-white rounded-lg transition cursor-pointer text-xs"
            title="Rata Kiri (Float Left)"
          >
            <AlignLeft className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => handleAlignSelectedImage("center")}
            className="p-1.5 hover:bg-slate-800 text-slate-300 hover:text-white rounded-lg transition cursor-pointer text-xs"
            title="Rata Tengah (Center)"
          >
            <AlignCenter className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => handleAlignSelectedImage("right")}
            className="p-1.5 hover:bg-slate-800 text-slate-300 hover:text-white rounded-lg transition cursor-pointer text-xs"
            title="Rata Kanan (Float Right)"
          >
            <AlignRight className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => handleAlignSelectedImage("full")}
            className="p-1.5 hover:bg-slate-800 text-slate-300 hover:text-white rounded-lg transition cursor-pointer text-xs"
            title="Ukuran Penuh (100% Lebar)"
          >
            <Maximize className="w-4 h-4" />
          </button>

          {/* Promote to Cover if callback provided */}
          {onSetCoverImage && selectedImageEl.src && (
            <>
              <div className="h-4 w-px bg-slate-700 mx-0.5" />
              <button
                type="button"
                onClick={() => {
                  onSetCoverImage(selectedImageEl.src);
                  alert("Foto ini telah disetel sebagai Gambar Sampul (Cover) artikel!");
                }}
                className="px-2 py-1 bg-emerald-700/80 hover:bg-emerald-600 text-white rounded-lg text-xs font-bold transition cursor-pointer"
                title="Jadikan foto ini sebagai Gambar Sampul artikel"
              >
                Cover
              </button>
            </>
          )}

          {/* Close toolbar */}
          <button
            type="button"
            onClick={() => {
              if (selectedImageEl) {
                selectedImageEl.classList.remove("ring-4", "ring-rose-500", "ring-offset-2", "shadow-xl");
              }
              setSelectedImageEl(null);
              setSelectedImgCoords(null);
            }}
            className="p-1 hover:bg-slate-800 text-slate-400 hover:text-white rounded-lg transition cursor-pointer ml-1"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Editor Main Content Area */}
      {mode === "visual" ? (
        <div
          ref={editorRef}
          contentEditable
          onInput={handleInput}
          onBlur={handleInput}
          onClick={handleEditorClick}
          onKeyUp={checkCurrentFormatting}
          onMouseUp={checkCurrentFormatting}
          onKeyDown={handleKeyDown}
          className="p-5 min-h-[300px] max-h-[550px] overflow-y-auto focus:outline-hidden article-visual-editor text-slate-800 text-base leading-relaxed bg-white"
          style={{
            wordBreak: "break-word",
          }}
          data-placeholder={placeholder}
        />
      ) : (
        <textarea
          id="wp-html-textarea"
          rows={14}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full p-4 min-h-[300px] font-mono text-xs bg-slate-950 text-sky-300 focus:outline-hidden leading-relaxed border-0"
        />
      )}

      {/* IN-BODY IMAGES MANAGER DRAWER / STRIP (Easy One-Click Photo Deletion) */}
      {embeddedImages.length > 0 && (
        <div className="bg-slate-50 border-t border-slate-200 p-3.5 space-y-2.5">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-lg bg-rose-100 text-rose-700 flex items-center justify-center font-bold text-xs">
                <ImageIcon className="w-3.5 h-3.5" />
              </span>
              <div>
                <h4 className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                  <span>Foto Terdeteksi di Dalam Kolom Naskah ({embeddedImages.length} Foto)</span>
                  <span className="px-1.5 py-0.5 bg-rose-100 text-rose-800 font-extrabold text-[10px] rounded-md">
                    Bisa Dihapus 1-Klik
                  </span>
                </h4>
                <p className="text-[11px] text-slate-500">
                  Klik tombol merah <strong>"Hapus Foto"</strong> di bawah untuk melenyapkan foto dari naskah artikel Anda.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleDeleteAllImages}
                className="px-2.5 py-1 bg-rose-600 hover:bg-rose-700 text-white rounded-lg text-xs font-bold flex items-center gap-1 shadow-2xs transition cursor-pointer"
              >
                <Trash2 className="w-3 h-3" />
                <span>Hapus Semua Foto</span>
              </button>
              <button
                type="button"
                onClick={() => setShowImageManager(!showImageManager)}
                className="p-1 hover:bg-slate-200 text-slate-600 rounded-lg transition cursor-pointer"
              >
                {showImageManager ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Grid of Embedded Images */}
          {showImageManager && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5 pt-1">
              {embeddedImages.map((img, idx) => (
                <div
                  key={img.id || idx}
                  className="bg-white p-2 rounded-xl border border-slate-200 shadow-2xs flex items-center gap-2.5 group hover:border-rose-300 transition"
                >
                  <div className="w-14 h-14 rounded-lg bg-slate-100 overflow-hidden shrink-0 border border-slate-200 relative">
                    <img
                      src={img.src}
                      alt={img.alt}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src =
                          "https://images.unsplash.com/photo-1499750310107-5fef28a66643?w=200";
                      }}
                    />
                    <span className="absolute top-0.5 left-0.5 bg-slate-900/80 text-white text-[9px] font-black px-1 rounded-sm">
                      #{idx + 1}
                    </span>
                  </div>

                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] font-bold text-slate-800 truncate" title={img.alt || img.src}>
                      {img.alt || `Foto Naskah ${idx + 1}`}
                    </p>
                    <p className="text-[10px] text-slate-400 font-mono truncate" title={img.src}>
                      {img.src}
                    </p>

                    <div className="flex items-center gap-1.5 mt-1.5">
                      {/* One-Click Delete Specific Image */}
                      <button
                        type="button"
                        onClick={() => handleDeleteImageByIndex(idx)}
                        className="px-2 py-0.5 bg-rose-50 hover:bg-rose-600 text-rose-700 hover:text-white rounded-md text-[10px] font-bold flex items-center gap-1 transition cursor-pointer border border-rose-200 hover:border-rose-600"
                        title="Hapus foto ini dari isi artikel"
                      >
                        <Trash2 className="w-2.5 h-2.5" />
                        <span>Hapus</span>
                      </button>

                      {/* Scroll to visual editor */}
                      <button
                        type="button"
                        onClick={() => handleScrollToImage(idx)}
                        className="px-2 py-0.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md text-[10px] font-medium transition cursor-pointer"
                        title="Sorot foto di tampilan editor"
                      >
                        Sorot
                      </button>

                      {/* Set as cover */}
                      {onSetCoverImage && (
                        <button
                          type="button"
                          onClick={() => {
                            onSetCoverImage(img.src);
                            alert("Foto ini disetel sebagai Gambar Sampul artikel!");
                          }}
                          className="px-2 py-0.5 bg-emerald-50 hover:bg-emerald-600 text-emerald-700 hover:text-white rounded-md text-[10px] font-medium transition cursor-pointer border border-emerald-200"
                          title="Jadikan gambar cover artikel"
                        >
                          Cover
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* SEO Status & Heading Hierarchy Summary Bar */}
      <div className="bg-slate-50 border-t border-slate-200 px-3 py-2 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-600">
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1.5 font-bold text-slate-800">
            <span className="w-2 h-2 rounded-full bg-emerald-500" />
            Blok Aktif Saat Ini:
            <strong className="px-2 py-0.5 rounded-md bg-slate-200 text-slate-900 uppercase font-black text-[11px]">
              {activeBlock === "p"
                ? "Paragraf (p)"
                : activeBlock === "h1"
                ? "Judul Utama (H1)"
                : activeBlock === "h2"
                ? "Subjudul Besar (H2)"
                : activeBlock === "h3"
                ? "Subjudul Sedang (H3)"
                : "Kutipan (Blockquote)"}
            </strong>
          </span>
        </div>

        <div className="flex items-center gap-4 text-[11px] text-slate-500 font-medium">
          <span>
            Foto Naskah: <strong className="text-slate-800 font-bold">{embeddedImages.length}</strong>
          </span>
          <span>
            Mode: <strong className="uppercase text-slate-800 font-bold">{mode}</strong>
          </span>
          <span>
            Panjang Karakter: <strong className="text-slate-800 font-bold">{(value || "").length}</strong>
          </span>
        </div>
      </div>
    </div>
  );
};
